/**
 * EL INFORME DE UNA VISITA O INSPECCIÓN (auditoría del portal del perito, 26-09-2026).
 *
 * `planner_tasks.visit_report` guarda dos cosas distintas que hasta hoy se mezclaban:
 *
 *  · El INFORME del perito (observaciones, propuestas, incidencias, checklist y
 *    fotos). Es suyo: lo escribe quien tiene la visita asignada. Si la visita no
 *    está asignada a nadie, lo escribe quien la cierra.
 *  · El COMENTARIO DE LA EMPRESA (`company_comment`): lo que el admin o el
 *    encargado quieren decir sobre ese informe, aparte, sin tocarlo.
 *
 * Y unas marcas que pone el servidor: quién la completó y cuándo
 * (`completed_by`/`completed_at`) y quién editó el informe por última vez
 * (`edited_by`/`edited_at`).
 *
 * Por qué existe este fichero: «Editar informe» del admin SUSTITUÍA el informe
 * entero por lo que hubiera en el formulario, y el formulario salía vacío porque
 * el planificador no pedía la columna. Guardar una línea borraba las
 * observaciones, las propuestas, las incidencias y las fotos del perito (C3,
 * T07-01, reproducido tres veces en producción). Ahora se FUSIONA: lo que no se
 * manda se conserva, y lo que no es del informe (el comentario, las marcas) no
 * se toca nunca desde el formulario del informe.
 *
 * La misma fusión vive en el servidor (`supabase/functions/_shared/visitReport.ts`,
 * la usa `update-task-status`); `visitReport.parity.test.ts` comprueba que las dos
 * dicen lo mismo.
 */

export type ChecklistStatus = "ok" | "ko" | "pending";

export interface VisitChecklistItem {
  item: string;
  status: ChecklistStatus;
}

export interface VisitCompanyComment {
  text: string;
  by?: string | null;
  at?: string | null;
}

export interface VisitReport {
  observations?: string;
  proposals?: string;
  incidents?: string;
  checklist?: VisitChecklistItem[];
  photos?: string[];
  completed_by?: string | null;
  completed_at?: string | null;
  edited_by?: string | null;
  edited_at?: string | null;
  company_comment?: VisitCompanyComment | null;
}

/** Lo que es del informe del perito. Todo lo demás (comentario, marcas) no se toca desde su formulario. */
export const VISIT_REPORT_CORE_KEYS = ["observations", "proposals", "incidents", "checklist", "photos"] as const;
export type VisitReportCoreKey = (typeof VISIT_REPORT_CORE_KEYS)[number];

export type VisitReportPatch = Partial<Record<VisitReportCoreKey, unknown>>;

const isEmptyValue = (v: unknown) =>
  v === null ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

/**
 * Fusiona lo que manda el formulario sobre lo que ya había.
 *  · clave AUSENTE en `patch` → se conserva lo que había;
 *  · clave con valor vacío (null, "", []) → se quita (el autor la vació a propósito);
 *  · clave con valor → se sustituye;
 *  · lo que no es del informe (`company_comment`, marcas de quién/cuándo) se
 *    conserva siempre: el formulario del informe no puede tocarlo.
 */
export function mergeVisitReport(
  existing: VisitReport | null | undefined,
  patch: VisitReportPatch,
  stamp?: Partial<Pick<VisitReport, "completed_by" | "completed_at" | "edited_by" | "edited_at">>,
): VisitReport {
  const base: Record<string, unknown> =
    existing && typeof existing === "object" && !Array.isArray(existing) ? { ...existing } : {};
  for (const key of VISIT_REPORT_CORE_KEYS) {
    if (!(key in patch)) continue;
    const value = patch[key];
    if (value === undefined) continue;
    if (isEmptyValue(value)) delete base[key];
    else base[key] = typeof value === "string" ? value.trim() : value;
  }
  if (stamp) {
    for (const [k, v] of Object.entries(stamp)) {
      if (v !== undefined) base[k] = v;
    }
  }
  return base as VisitReport;
}

/** ¿Hay algo escrito en el informe? (Una visita no se cierra vacía: T07-06.) */
export function visitReportHasContent(report: VisitReport | null | undefined, extraPhotos = 0): boolean {
  if (!report || typeof report !== "object") return extraPhotos > 0;
  const text = [report.observations, report.proposals, report.incidents].some(
    (v) => typeof v === "string" && v.trim().length > 0,
  );
  const checklist = Array.isArray(report.checklist) && report.checklist.length > 0;
  const photos = (Array.isArray(report.photos) ? report.photos.length : 0) + extraPhotos > 0;
  return text || checklist || photos;
}

/**
 * ¿Puede esta persona escribir el INFORME de la visita?
 * El informe es de quien tiene la visita asignada. Si no está asignada a nadie,
 * lo escribe quien la pueda cerrar (la pantalla ya solo ofrece el botón a quien puede).
 */
export function canWriteVisitReport(task: { assigned_to?: string | null } | null | undefined, userId: string | null | undefined): boolean {
  if (!task || !userId) return false;
  if (!task.assigned_to) return true;
  return task.assigned_to === userId;
}

/** El comentario de la empresa lo dejan el admin y el encargado. */
export function canCommentVisitReport(role: string | null | undefined): boolean {
  return role === "admin" || role === "manager";
}

/** Solo las visitas y las inspecciones llevan informe. */
export function isVisitLike(type: string | null | undefined): boolean {
  return type === "visit" || type === "inspection";
}

/**
 * La clave de traducción del estado de una tarea. La BD guarda `in_progress`,
 * pero la clave de `planner.statuses` es `inProgress`: la ficha enseñaba el texto
 * crudo «planner.statuses.in_progress» (T07-08).
 */
export function taskStatusLabelKey(status: string | null | undefined): string {
  const s = status || "pending";
  if (s === "in_progress") return "planner.statuses.inProgress";
  return `planner.statuses.${s}`;
}

/** Visitas que aún se pueden completar: pendientes y en curso (la «en curso» no tenía botón: T07-08). */
export function isVisitOpen(status: string | null | undefined): boolean {
  return status === "pending" || status === "in_progress" || !status;
}

/** Las fotos del informe: las del propio informe y, si no, la columna `photos`. */
export function visitPhotos(task: { visit_report?: unknown; photos?: string[] | null } | null | undefined): string[] {
  if (!task) return [];
  const report = task.visit_report as VisitReport | null | undefined;
  const fromReport = Array.isArray(report?.photos) ? (report!.photos as string[]) : [];
  const fromColumn = Array.isArray(task.photos) ? task.photos : [];
  return [...new Set([...fromReport, ...fromColumn])];
}
